import React, { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '../../../../lib/supabase'; // Adjusted path
import { supabaseLotService } from '../../../../services/supabase/lots'; // Adjusted path
import type { Database } from '../../../../types/supabase'; // Import Database type

// Define a type for a row from the 'lots' table as defined in supabase.ts
type LandLotRow = Database['public']['Tables']['lots']['Row'];

// Define a simpler Lot type for this modal's display needs
// Note: The service functions actually return more fields than defined in 'lots':'Row'
// because they select directly from the 'land_lots' SQL table.
// We'll keep DisplayLot flexible with Partial<LandLotRow> and add expected fields.
interface DisplayLot extends Partial<LandLotRow> { // LandLotRow now refers to 'lots':'Row'
  lot_id: string; // This should align with 'id' from 'lots':'Row' if using raw type
                  // However, our service selects 'lot_id'. We need to reconcile this.
  
  // Fields selected by supabaseLotService that might not be in 'lots':'Row' directly
  // or have different names.
  lot_desc?: string | null; 
  lot_perm_use?: string | null;
  lot_community?: string | null; // 'community_id' in 'lots':'Row'

  community_name?: string | null;
  leaselot_use?: string | null; // Use from lease_lots if available
}

interface ViewUnderLeaseLotsModalProps {
  isOpen: boolean;
  onClose: () => void;
  subleaseId: string; // This is the parent sublease.lease_id
  underLeaseIdentifier?: string; // Optional: e.g., underlease_id or a descriptive name
}

export default function ViewUnderLeaseLotsModal({ 
  isOpen, 
  onClose, 
  subleaseId,
  underLeaseIdentifier 
}: ViewUnderLeaseLotsModalProps) {
  const [lots, setLots] = useState<DisplayLot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Helper to enhance lot data with community names
  const enhanceLotsWithCommunityNames = async (lotsData: any[]) => { // Use any[] for now due to type mismatch
    const communityIds = lotsData
      .map(lot => lot.lot_community) // Assumes service returns 'lot_community'
      .filter((id): id is string => id !== null && id !== undefined);

    let communityMap: Record<string, string> = {};
    if (communityIds.length > 0) {
      try {
        const { data: communities, error: commError } = await supabase
          .from('communities')
          .select('comm_id, comm_name')
          .in('comm_id', communityIds);
        if (!commError && communities) {
          communityMap = communities.reduce((map, comm) => {
            map[comm.comm_id] = comm.comm_name;
            return map;
          }, {} as Record<string, string>);
        } else if (commError) console.warn('Could not fetch community names for lots:', commError);
      } catch (commFetchError) {
        console.error('Error fetching communities for lots:', commFetchError);
      }
    }
    return lotsData.map(lot => ({
      ...lot,
      lot_id: lot.lot_id, // Service returns 'lot_id'
      community_name: lot.lot_community ? communityMap[lot.lot_community] || `Community ${lot.lot_community}` : 'Unknown'
    }));
  };

  useEffect(() => {
    if (isOpen && subleaseId) {
      setIsLoading(true);
      setRequestError(null);
      setLots([]);

      const fetchAssociatedLots = async () => {
        try {
          // 1. Get lot_ids from lease_lots for the parent sublease
          const { data: leaseLotLinks, error: leaseLotError } = await supabase
            .from('lease_lots')
            .select('leaselot_lot, leaselot_use')
            .eq('leaselot_lease', subleaseId);

          if (leaseLotError) {
            console.error('Error fetching lease_lots:', leaseLotError);
            throw new Error(`Failed to fetch lot associations: ${leaseLotError.message}`);
          }

          if (!leaseLotLinks || leaseLotLinks.length === 0) {
            setLots([]);
            setIsLoading(false);
            return;
          }

          const lotIds = leaseLotLinks.map(link => link.leaselot_lot);
          const lotUseMap = leaseLotLinks.reduce((map, link) => {
            map[link.leaselot_lot] = link.leaselot_use;
            return map;
          }, {} as Record<string, string | null>);

          // 2. Get full lot details for these lot_ids
          const lotDetailsData = await supabaseLotService.getLotsByIds(lotIds);
          
          // 3. Enhance with community names and merge leaselot_use
          const lotDetailsFromService = lotDetailsData as any[]; // Cast to handle potential mismatch for now
          const enhancedLotDetails = await enhanceLotsWithCommunityNames(lotDetailsFromService);
          
          const displayLotsData: DisplayLot[] = enhancedLotDetails.map(lot => ({
            lot_id: lot.lot_id, // Explicitly map from service result
            lot_desc: lot.lot_desc,
            lot_perm_use: lot.lot_perm_use,
            lot_community: lot.lot_community,
            community_name: lot.community_name,
            leaselot_use: lotUseMap[lot.lot_id] || lot.lot_perm_use // Prioritize use from lease_lots
          }));

          setLots(displayLotsData);

        } catch (err) {
          console.error('Error fetching associated lots for underlease:', err);
          setRequestError(err instanceof Error ? err.message : 'Failed to load lots data.');
        } finally {
          setIsLoading(false);
        }
      };

      fetchAssociatedLots();
    }
  }, [isOpen, subleaseId]);

  if (!isOpen) return null;

  const modalTitle = underLeaseIdentifier 
    ? `Lots for UnderLease (via SubLease #${subleaseId})` 
    : `Lots for Parent SubLease #${subleaseId}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary" />
              <h2 className="text-2xl font-bold text-gray-800">
                {modalTitle}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md">
            <div className="flex items-start">
              <Info className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Lot Association Information</p>
                <p className="text-sm mt-1">
                  These are the lots associated with the parent SubLease (ID: {subleaseId}). 
                  Underleases inherit lot associations from their parent SubLease.
                </p>
              </div>
            </div>
          </div>

          {requestError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error Loading Lots</p>
                  <p>{requestError}</p>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : lots.length === 0 && !requestError ? (
            <div className="text-center py-8 text-gray-500">
              No lots are associated with the parent SubLease #{subleaseId}.
            </div>
          ) : lots.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lot ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Community
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permitted Use (from LeaseLot)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lot Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lots.map((lot) => (
                    <tr key={lot.lot_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lot.lot_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lot.community_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {lot.leaselot_use || lot.lot_perm_use || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {lot.lot_desc || 'No description'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
